'use client'

import { useEffect, useRef, type RefObject } from 'react'

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])'

export type UseFocusTrapOptions = {
  /** Whether the trap is live — typically the modal's `open`. */
  active: boolean
  /** Ref to the element whose focusable children are trapped (the dialog card). */
  containerRef: RefObject<HTMLElement | null>
  /**
   * Called on Escape. Pass `undefined` to disable Escape-to-close — e.g. while a
   * submit is in flight. Tab-trapping and focus restore stay active regardless.
   */
  onEscape?: () => void
}

/**
 * Focus management for modal dialogs. On activate: remember the invoker and move
 * focus into the container. While active: trap Tab within the container and,
 * when `onEscape` is provided, close on Escape. On deactivate/unmount: restore
 * focus to the invoker.
 *
 * Extracted from the storefront's hand-rolled account modals (ChangeEmailModal,
 * CopyToOtherDealersModal) and PopupShell's modal/takeover branch, which each
 * carried a byte-identical copy of this effect. Consumed by `ModalShell`.
 *
 * `onEscape` is read through a ref so passing a fresh inline callback every
 * render does NOT re-run the effect (which would re-focus the first element and
 * re-record the invoker on each render). The effect re-runs only when `active`
 * toggles.
 */
export function useFocusTrap({ active, containerRef, onEscape }: UseFocusTrapOptions): void {
  const onEscapeRef = useRef(onEscape)
  // Keep the ref current via an effect (not a write-in-render) — `onEscapeRef`
  // is read only inside the async keydown handler below, which never fires
  // before this post-render effect has run.
  useEffect(() => {
    onEscapeRef.current = onEscape
  })

  useEffect(() => {
    if (!active) return
    const node = containerRef.current
    if (!node) return

    const invoker = document.activeElement as HTMLElement | null

    // Move focus into the dialog: first focusable child, else the card itself.
    const firstFocusable = node.querySelector<HTMLElement>(FOCUSABLE)
    if (firstFocusable) {
      firstFocusable.focus()
    } else {
      node.setAttribute('tabindex', '-1')
      node.focus()
    }

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        const onEsc = onEscapeRef.current
        if (onEsc) {
          e.preventDefault()
          onEsc()
        }
        return
      }
      if (e.key !== 'Tab') return
      const focusables = node!.querySelectorAll<HTMLElement>(FOCUSABLE)
      if (focusables.length === 0) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      if (invoker && typeof invoker.focus === 'function') invoker.focus()
    }
  }, [active, containerRef])
}
